import { Heading, HStack } from "@chakra-ui/layout";
import { Trans, t } from "@lingui/macro";
import {
  type ExpenseBudgetItem,
  ExpenseStatusEnum,
} from "@src/__generated__/urql-graphql";
import { ExpenseStatusBadge } from "@src/components/modules/expenses/listing/table/ExpenseStatusBadge";
import { UnapprovedExpensesLinkButton } from "@src/components/modules/projects/detail/BudgetItems/Expense/ExpenseItem";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
} from "@src/components/ui-kit";
import { currency, dateTime } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useMemo } from "react";
import { LongContentCell } from "../../TableCell";
import type { ExpenseItem } from "./models";

type TableExpenseStatsRow = ExpenseItem["expenseStats"][0];
type TableExpenseStatsRowContext = {
  row: {
    original: TableExpenseStatsRow;
  };
};

interface TableExpenseStatsProps {
  id: ExpenseBudgetItem["id"];
  expenseItem: ExpenseItem;
}

const TableExpenseStats = ({ id, expenseItem }: TableExpenseStatsProps) => {
  const columns = useMemo<BodyColumnDef<TableExpenseStatsRow>[]>(
    () =>
      [
        {
          id: "id",
          meta: { label: t`ID` },
          header: "ID",
          cell: ({ row }: TableExpenseStatsRowContext) => (
            <Link
              href={{
                pathname: "/expenses/edit",
                query: { id: row.original.expense.id },
              }}
              style={{
                width: "5rem",
              }}
            >
              {row.original.expense.id}
            </Link>
          ),
        },
        {
          id: "data",
          meta: { label: t`Date` },
          header: () => <Trans>Date</Trans>,
          cell: ({ row }: TableExpenseStatsRowContext) =>
            !!row.original.date && dateTime.format(new Date(row.original.date)),
        },
        {
          id: "contractor",
          meta: { label: t`Contractor` },
          header: () => <Trans>Contractor</Trans>,
          cell: ({ row }: TableExpenseStatsRowContext) =>
            row.original.contractor && (
              <LongContentCell maxWidth="25rem">
                <Link
                  href={{
                    pathname: "/contractors/detail",
                    query: { id: row.original.contractor.id },
                  }}
                >
                  {row.original.contractor.legal_name}
                </Link>
              </LongContentCell>
            ),
        },
        {
          id: "status",
          meta: { label: t`Status` },
          header: () => <Trans>Status</Trans>,
          cell: ({ row }: TableExpenseStatsRowContext) => (
            <ExpenseStatusBadge status={row.original.expense.status} />
          ),
        },
        {
          id: "amount",
          meta: { label: t`Amount`, align: "right" },
          header: () => <Trans>Amount</Trans>,
          cell: ({ row }: TableExpenseStatsRowContext) =>
            currency.formatByWorkspace(row.original.amount),
        },
        {
          id: ACTIONS_COLUMN_ID,
          meta: { label: t`Actions` },
        },
      ].map((column) => {
        if (column.id === "id") {
          return {
            ...column,
            footer: () =>
              expenseItem.expenseStats.some(
                ({ expense }) => expense.status !== ExpenseStatusEnum.Approved,
              ) && <UnapprovedExpensesLinkButton expenseId={id} />,
          } satisfies BodyColumnDef<TableExpenseStatsRow>;
        }

        if (column.id === "status") {
          return {
            ...column,
            footer: () => <span className="text-inkLighter">{t`Total`}</span>,
          } satisfies BodyColumnDef<TableExpenseStatsRow>;
        }

        if (column.id === "amount") {
          return {
            ...column,
            footer: () => (
              <span className="text-2xl">
                {currency.formatByWorkspace(expenseItem.stats.real_expenses)}
              </span>
            ),
          } satisfies BodyColumnDef<TableExpenseStatsRow>;
        }

        return column;
      }),
    [expenseItem.expenseStats, expenseItem.stats.real_expenses, id],
  );

  return (
    <DataTable>
      <DataTable.NavBar>
        <HStack py="4" justify="space-between">
          <Heading size="sm">
            <Trans>Expenses</Trans>
          </Heading>
        </HStack>
      </DataTable.NavBar>
      <DataTable.Body
        tableKey="expenseStats"
        data={expenseItem.expenseStats}
        columns={columns}
      />
    </DataTable>
  );
};

export default observer(TableExpenseStats);
